import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useNotification } from '../../contexts/NotificationContext';

const LandingEditor = () => {
  const { confirm } = useNotification();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [msg, setMsg] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/landing-content`, { headers });
      setContent(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSection = async (section, data) => {
    setSaving(true);
    setMsg('');
    try {
      await axios.put(`${API}/admin/landing-content/${section}`, data, { headers });
      setMsg('Guardado correctamente');
      await load();
    } catch (e) { setMsg('Error al guardar'); console.error(e); }
    finally { setSaving(false); setTimeout(() => setMsg(''), 3000); }
  };

  const resetAll = async () => {
    if (!(await confirm('¿Restaurar todo al contenido por defecto?', { confirmLabel: 'Restaurar' }))) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/landing-content/reset`, {}, { headers });
      await load();
      setMsg('Contenido restaurado');
    } catch (e) { setMsg('Error al restaurar'); }
    finally { setSaving(false); setTimeout(() => setMsg(''), 3000); }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Cargando contenido...</div>;
  if (!content) return <div className="text-center py-12 text-red-500">Error al cargar contenido</div>;

  const tabs = [
    { key: 'hero', label: 'Hero' },
    { key: 'how_it_works', label: 'Cómo funciona' },
    { key: 'slider', label: 'Slider Producto' },
    { key: 'clients', label: 'Clientes' },
    { key: 'form', label: 'Formulario' },
  ];

  return (
    <div className="w-full" data-testid="landing-editor">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Editor de Landing Page</h1>
          <p className="text-sm text-gray-500 mt-1">Edita el contenido de cada sección de la landing page</p>
        </div>
        <button onClick={resetAll} disabled={saving} className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors" data-testid="reset-landing-btn">
          Restaurar por defecto
        </button>
      </div>

      {msg && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${msg.includes('Error') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`} data-testid="landing-msg">
          {msg}
        </div>
      )}

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveSection(t.key)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${activeSection === t.key ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {activeSection === 'hero' && <HeroEditor data={content.hero} onSave={d => saveSection('hero', d)} saving={saving} />}
      {activeSection === 'how_it_works' && <HowItWorksEditor data={content.how_it_works} onSave={d => saveSection('how_it_works', d)} saving={saving} />}
      {activeSection === 'slider' && <SliderEditor data={content.slider} onSave={d => saveSection('slider', d)} saving={saving} />}
      {activeSection === 'clients' && <ClientsEditor data={content.clients} onSave={d => saveSection('clients', d)} saving={saving} />}
      {activeSection === 'form' && <FormEditor data={content.form} onSave={d => saveSection('form', d)} saving={saving} />}
    </div>
  );
};

/* ═══ Section Editors ═══ */

const Field = ({ label, value, onChange, type = 'text', placeholder = '', rows }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    {type === 'textarea' ? (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows || 3} placeholder={placeholder} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-all resize-none" />
    ) : (
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-all" />
    )}
  </div>
);

const SaveBtn = ({ onClick, saving }) => (
  <button onClick={onClick} disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50" data-testid="save-section-btn">
    {saving ? 'Guardando...' : 'Guardar cambios'}
  </button>
);

/* ─── Drag & Drop Image Upload ─── */
const ImageUpload = ({ currentUrl, onUpload, label = 'Imagen' }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileRef = React.useRef(null);
  const token = localStorage.getItem('token');

  const upload = async (file) => {
    if (!file || !file.type.startsWith('image/')) { setError('Solo archivos de imagen'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Máximo 10MB'); return; }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/landing-content/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      onUpload(res.data.url);
    } catch (e) { setError('Error al subir imagen'); console.error(e); }
    finally { setUploading(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) upload(e.dataTransfer.files[0]); };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div
        onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
        onClick={() => fileRef.current?.click()}
        className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`}
        data-testid="image-upload-zone"
      >
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && upload(e.target.files[0])} />
        {uploading ? (
          <div className="py-4 text-sm text-blue-600 font-medium">Subiendo imagen...</div>
        ) : currentUrl ? (
          <div className="flex items-center gap-3">
            <img src={currentUrl} alt="Preview" className="w-16 h-12 object-cover rounded-md border border-gray-200" />
            <div className="text-left flex-1">
              <p className="text-xs text-gray-500 truncate max-w-[200px]">{currentUrl}</p>
              <p className="text-xs text-blue-500 mt-1">Arrastra o haz clic para cambiar</p>
            </div>
          </div>
        ) : (
          <div className="py-4">
            <svg className="w-8 h-8 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            <p className="text-sm text-gray-400">Arrastra una imagen o haz clic para seleccionar</p>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

const HeroEditor = ({ data, onSave, saving }) => {
  const [hero, setHero] = useState(data || {});
  const update = (k, v) => setHero(p => ({ ...p, [k]: v }));
  const updateBtn = (i, k, v) => {
    const btns = [...(hero.buttons || [])];
    btns[i] = { ...btns[i], [k]: v };
    update('buttons', btns);
  };
  const removeBtn = (i) => update('buttons', (hero.buttons || []).filter((_, idx) => idx !== i));
  const addBtn = () => update('buttons', [...(hero.buttons || []), { label: '', action: 'navigate', target: '/', style: 'ghost', show_discount: false }]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="hero-editor">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Sección Hero</h3>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Field label="Título principal" value={hero.headline} onChange={v => update('headline', v)} />
          <Field label="Texto con acento (amarillo)" value={hero.headline_accent} onChange={v => update('headline_accent', v)} />
          <Field label="Subtítulo" value={hero.subtitle} onChange={v => update('subtitle', v)} type="textarea" />
          <ImageUpload label="Imagen del Hero" currentUrl={hero.image_url} onUpload={v => update('image_url', v)} />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Botones ({(hero.buttons || []).length})</h4>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {(hero.buttons || []).map((btn, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input value={btn.label || ''} onChange={e => updateBtn(i, 'label', e.target.value)} placeholder="Etiqueta" className="px-2 py-1.5 border border-gray-200 rounded text-sm" />
                  <select value={btn.style || 'primary'} onChange={e => updateBtn(i, 'style', e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded text-sm">
                    <option value="primary">Principal</option>
                    <option value="outline">Outline</option>
                    <option value="ghost">Ghost</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <select value={btn.action || 'navigate'} onChange={e => updateBtn(i, 'action', e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded text-sm">
                    <option value="navigate">Navegar</option>
                    <option value="scroll">Scroll a sección</option>
                    <option value="external">URL externa</option>
                  </select>
                  <input value={btn.target || ''} onChange={e => updateBtn(i, 'target', e.target.value)} placeholder="/ruta o #seccion" className="px-2 py-1.5 border border-gray-200 rounded text-sm" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-gray-500">
                    <input type="checkbox" checked={btn.show_discount || false} onChange={e => updateBtn(i, 'show_discount', e.target.checked)} />
                    Mostrar descuento
                  </label>
                  <button onClick={() => removeBtn(i)} className="text-xs text-red-500 hover:text-red-700">Eliminar</button>
                </div>
              </div>
            ))}
          </div>
          <button onClick={addBtn} className="mt-3 w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors">+ Agregar botón</button>
        </div>
      </div>
      <div className="mt-6 flex justify-end"><SaveBtn onClick={() => onSave(hero)} saving={saving} /></div>
    </div>
  );
};

const SliderEditor = ({ data, onSave, saving }) => {
  const [slider, setSlider] = useState(data || {});
  const update = (k, v) => setSlider(p => ({ ...p, [k]: v }));
  const updateSlide = (i, k, v) => {
    const slides = [...(slider.slides || [])];
    slides[i] = { ...slides[i], [k]: v };
    update('slides', slides);
  };
  const removeSlide = (i) => update('slides', (slider.slides || []).filter((_, idx) => idx !== i));
  const addSlide = () => update('slides', [...(slider.slides || []), { title: '', description: '', image_url: '', order: (slider.slides || []).length }]);
  const moveSlide = (i, dir) => {
    const slides = [...(slider.slides || [])];
    const j = i + dir;
    if (j < 0 || j >= slides.length) return;
    [slides[i], slides[j]] = [slides[j], slides[i]];
    update('slides', slides);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="slider-editor">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Slider de Producto</h3>
      <div className="grid grid-cols-2 gap-6 mb-6">
        <Field label="Título de sección" value={slider.section_title} onChange={v => update('section_title', v)} />
        <Field label="Subtítulo" value={slider.section_subtitle} onChange={v => update('section_subtitle', v)} />
      </div>
      <Field label="Velocidad autoplay (ms)" value={slider.autoplay_speed} onChange={v => update('autoplay_speed', parseInt(v) || 4000)} type="number" />
      <h4 className="text-sm font-semibold text-gray-700 mb-3 mt-4">Slides ({(slider.slides || []).length})</h4>
      <div className="space-y-3">
        {(slider.slides || []).map((slide, i) => (
          <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
            <div className="grid grid-cols-3 gap-3 mb-2">
              <input value={slide.title || ''} onChange={e => updateSlide(i, 'title', e.target.value)} placeholder="Título" className="px-2 py-1.5 border border-gray-200 rounded text-sm" />
              <input value={slide.description || ''} onChange={e => updateSlide(i, 'description', e.target.value)} placeholder="Descripción" className="px-2 py-1.5 border border-gray-200 rounded text-sm col-span-2" />
            </div>
            <div className="flex items-center gap-2">
              <ImageUpload label="" currentUrl={slide.image_url} onUpload={v => updateSlide(i, 'image_url', v)} />
            </div>
            <div className="flex items-center gap-2 mt-1">
              <button onClick={() => moveSlide(i, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30">&uarr;</button>
              <button onClick={() => moveSlide(i, 1)} disabled={i === (slider.slides || []).length - 1} className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30">&darr;</button>
              <button onClick={() => removeSlide(i)} className="text-xs text-red-500 hover:text-red-700 px-2">Eliminar</button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={addSlide} className="mt-3 w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors">+ Agregar slide</button>
      <div className="mt-6 flex justify-end"><SaveBtn onClick={() => onSave(slider)} saving={saving} /></div>
    </div>
  );
};

const ClientsEditor = ({ data, onSave, saving }) => {
  const [clients, setClients] = useState(data || {});
  const update = (k, v) => setClients(p => ({ ...p, [k]: v }));
  const updateLogo = (i, k, v) => {
    const logos = [...(clients.logos || [])];
    logos[i] = { ...logos[i], [k]: v };
    update('logos', logos);
  };
  const removeLogo = (i) => update('logos', (clients.logos || []).filter((_, idx) => idx !== i));
  const addLogo = () => update('logos', [...(clients.logos || []), { name: '', image_url: '', order: (clients.logos || []).length }]);
  const moveLogo = (i, dir) => {
    const logos = [...(clients.logos || [])];
    const j = i + dir;
    if (j < 0 || j >= logos.length) return;
    [logos[i], logos[j]] = [logos[j], logos[i]];
    update('logos', logos);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="clients-editor">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Sección Clientes</h3>
      <Field label="Título de sección" value={clients.section_title} onChange={v => update('section_title', v)} />
      <Field label="Subtítulo" value={clients.section_subtitle} onChange={v => update('section_subtitle', v)} type="textarea" />
      <Field label="Texto del CTA" value={clients.cta_text} onChange={v => update('cta_text', v)} />
      <h4 className="text-sm font-semibold text-gray-700 mb-3 mt-4">Logos ({(clients.logos || []).length})</h4>
      <div className="grid grid-cols-2 gap-3">
        {(clients.logos || []).map((logo, i) => (
          <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              {logo.image_url && <img src={logo.image_url} alt={logo.name} className="w-16 h-10 object-contain rounded" />}
              <input value={logo.name || ''} onChange={e => updateLogo(i, 'name', e.target.value)} placeholder="Nombre" className="flex-1 px-2 py-1 border border-gray-200 rounded text-sm" />
              <button onClick={() => moveLogo(i, -1)} disabled={i === 0} className="text-gray-400 hover:text-blue-600 disabled:opacity-30">&uarr;</button>
              <button onClick={() => moveLogo(i, 1)} disabled={i === (clients.logos || []).length - 1} className="text-gray-400 hover:text-blue-600 disabled:opacity-30">&darr;</button>
              <button onClick={() => removeLogo(i)} className="text-xs text-red-500 hover:text-red-700">X</button>
            </div>
            <ImageUpload label="" currentUrl={logo.image_url} onUpload={v => updateLogo(i, 'image_url', v)} />
          </div>
        ))}
      </div>
      <button onClick={addLogo} className="mt-3 w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors">+ Agregar logo</button>
      <div className="mt-6 flex justify-end"><SaveBtn onClick={() => onSave(clients)} saving={saving} /></div>
    </div>
  );
};

const FormEditor = ({ data, onSave, saving }) => {
  const [form, setForm] = useState(data || {});
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const updateBenefit = (i, k, v) => {
    const benefits = [...(form.benefits || [])];
    benefits[i] = { ...benefits[i], [k]: v };
    update('benefits', benefits);
  };
  const removeBenefit = (i) => update('benefits', (form.benefits || []).filter((_, idx) => idx !== i));
  const addBenefit = () => update('benefits', [...(form.benefits || []), { title: '', description: '' }]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="form-editor">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Sección Formulario</h3>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Field label="Título" value={form.section_title} onChange={v => update('section_title', v)} />
          <Field label="Subtítulo" value={form.section_subtitle} onChange={v => update('section_subtitle', v)} type="textarea" />
          <Field label="Texto del botón" value={form.cta_text} onChange={v => update('cta_text', v)} />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Beneficios ({(form.benefits || []).length})</h4>
          <div className="space-y-3 max-h-[360px] overflow-y-auto">
            {(form.benefits || []).map((b, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <input value={b.title || ''} onChange={e => updateBenefit(i, 'title', e.target.value)} placeholder="Título" className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm mb-2" />
                <textarea value={b.description || ''} onChange={e => updateBenefit(i, 'description', e.target.value)} placeholder="Descripción" rows={2} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm resize-none" />
                <button onClick={() => removeBenefit(i)} className="mt-1 text-xs text-red-500 hover:text-red-700">Eliminar</button>
              </div>
            ))}
          </div>
          <button onClick={addBenefit} className="mt-3 w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors">+ Agregar beneficio</button>
        </div>
      </div>
      <div className="mt-6 flex justify-end"><SaveBtn onClick={() => onSave(form)} saving={saving} /></div>
    </div>
  );
};

const HowItWorksEditor = ({ data, onSave, saving }) => {
  const [hiw, setHiw] = useState(data || { phones: [], bullets: [], primary_cta: {}, secondary_cta: {} });
  const update = (k, v) => setHiw(p => ({ ...p, [k]: v }));

  const updateBullet = (i, v) => {
    const bs = [...(hiw.bullets || [])];
    bs[i] = { ...bs[i], text: v, order: i };
    update('bullets', bs);
  };
  const removeBullet = (i) => update('bullets', (hiw.bullets || []).filter((_, idx) => idx !== i));
  const addBullet = () => update('bullets', [...(hiw.bullets || []), { text: '', order: (hiw.bullets || []).length }]);

  const updatePhone = (i, k, v) => {
    const ph = [...(hiw.phones || [])];
    ph[i] = { ...ph[i], [k]: v, order: i };
    update('phones', ph);
  };
  const ensurePhones = () => {
    const ph = [...(hiw.phones || [])];
    while (ph.length < 2) ph.push({ image_url: '', label: '', order: ph.length });
    return ph;
  };
  const phones = ensurePhones();

  const updateCta = (which, k, v) => {
    update(which, { ...(hiw[which] || {}), [k]: v });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="howitworks-editor">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Sección Cómo Funciona (NFC + QR)</h3>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Field label="Eyebrow / pre-título" value={hiw.eyebrow} onChange={v => update('eyebrow', v)} placeholder="Cómo funciona" />
          <Field label="Título" value={hiw.headline} onChange={v => update('headline', v)} placeholder="Acerca, comparte" />
          <Field label="Frase con énfasis (cursiva)" value={hiw.headline_accent} onChange={v => update('headline_accent', v)} placeholder="y sigue conectado." />
          <Field label="Subtítulo" value={hiw.subtitle} onChange={v => update('subtitle', v)} type="textarea" rows={3} />

          <h4 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Bullets ({(hiw.bullets || []).length})</h4>
          <div className="space-y-2 mb-3">
            {(hiw.bullets || []).map((b, i) => (
              <div key={i} className="flex gap-2">
                <input value={b.text || ''} onChange={e => updateBullet(i, e.target.value)} placeholder="Texto del bullet" className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-sm" />
                <button onClick={() => removeBullet(i)} className="text-xs text-red-500 hover:text-red-700 px-2">X</button>
              </div>
            ))}
          </div>
          <button onClick={addBullet} className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors">+ Agregar bullet</button>

          <h4 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Botones</h4>
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 mb-2">
            <p className="text-xs font-semibold text-gray-600 mb-1.5">Botón principal</p>
            <div className="grid grid-cols-2 gap-2">
              <input value={(hiw.primary_cta || {}).label || ''} onChange={e => updateCta('primary_cta', 'label', e.target.value)} placeholder="Etiqueta" className="px-2 py-1.5 border border-gray-200 rounded text-sm" />
              <select value={(hiw.primary_cta || {}).action || 'navigate'} onChange={e => updateCta('primary_cta', 'action', e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded text-sm">
                <option value="navigate">Navegar</option>
                <option value="scroll">Scroll a sección</option>
                <option value="external">URL externa</option>
              </select>
            </div>
            <input value={(hiw.primary_cta || {}).target || ''} onChange={e => updateCta('primary_cta', 'target', e.target.value)} placeholder="/ruta o #seccion" className="w-full mt-2 px-2 py-1.5 border border-gray-200 rounded text-sm" />
          </div>
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-xs font-semibold text-gray-600 mb-1.5">Botón secundario</p>
            <div className="grid grid-cols-2 gap-2">
              <input value={(hiw.secondary_cta || {}).label || ''} onChange={e => updateCta('secondary_cta', 'label', e.target.value)} placeholder="Etiqueta" className="px-2 py-1.5 border border-gray-200 rounded text-sm" />
              <select value={(hiw.secondary_cta || {}).action || 'navigate'} onChange={e => updateCta('secondary_cta', 'action', e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded text-sm">
                <option value="navigate">Navegar</option>
                <option value="scroll">Scroll a sección</option>
                <option value="external">URL externa</option>
              </select>
            </div>
            <input value={(hiw.secondary_cta || {}).target || ''} onChange={e => updateCta('secondary_cta', 'target', e.target.value)} placeholder="/ruta o #seccion" className="w-full mt-2 px-2 py-1.5 border border-gray-200 rounded text-sm" />
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Mockups de celulares (2)</h4>
          <p className="text-xs text-gray-500 mb-3">Sube las imágenes/screenshots que aparecerán dentro de cada celular. Recomendado: imágenes verticales 9:19.</p>
          {[0, 1].map(i => (
            <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-100 mb-3">
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Celular {i + 1}</p>
              <input value={phones[i].label || ''} onChange={e => updatePhone(i, 'label', e.target.value)} placeholder="Etiqueta superior (ej: 'Acerca tu tarjeta')" className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm mb-2" />
              <ImageUpload label="Imagen del celular" currentUrl={phones[i].image_url} onUpload={v => updatePhone(i, 'image_url', v)} />
            </div>
          ))}

          <h4 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Tarjeta flotante</h4>
          <label className="flex items-center gap-2 text-xs text-gray-600 mb-2">
            <input type="checkbox" checked={hiw.show_floating_card !== false} onChange={e => update('show_floating_card', e.target.checked)} />
            Mostrar tarjeta flotante entre los celulares
          </label>
          <Field label="Texto en la tarjeta" value={hiw.floating_card_label} onChange={v => update('floating_card_label', v)} placeholder="CONTACT" />
        </div>
      </div>
      <div className="mt-6 flex justify-end"><SaveBtn onClick={() => onSave(hiw)} saving={saving} /></div>
    </div>
  );
};

export default LandingEditor;
